import { useAuthStore } from '@/features/auth/auth-store';

import {
  REFERRAL_STATUS_META,
  type Referral,
  effectiveOwner,
  timeAgoLabel,
} from './referrals-store';
import { menuItemsFor, type ReferralMenuAction } from './referral-menu';

/**
 * Status-mini dot colors for the TABLE variant (SCRUM-331 handoff note): the
 * v6 mini uses GOLD for pending — unlike the dashboard row dot, which is
 * amber. Reviewed/success/denied use the shared status tokens.
 */
const MINI_DOT: Record<string, string> = {
  pending: 'bg-brand-primary',
  reviewed: 'bg-status-reviewed',
  success: 'bg-status-success',
  denied: 'bg-status-denied',
};

export interface ReferralsTableProps {
  referrals: Referral[];
  /** Customer id currently open in the inspector drawer (row highlight). */
  inspectingCustomerId: string | null;
  selectedIds: ReadonlySet<string>;
  menuOpenId: string | null;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onToggleMenu: (id: string | null) => void;
  onRowOpen: (referral: Referral) => void;
  onMenuAction: (referral: Referral, action: ReferralMenuAction) => void;
}

/**
 * The referrals console table (SCRUM-331) — v6 `.referrals-table`: mono
 * uppercase headers on bg-4, manual corner rounding (no overflow clipping so
 * the row menu can escape), per-status row states (new-dot bold rows,
 * inspecting gold tint + inset accent, selected brand tint), italic notes
 * with the DENIED reason sub-block, mono submitted cell, status-mini,
 * avatar assignee cells ("Branch Manager · default" when unassigned), and
 * the per-status "…" actions menu.
 */
export function ReferralsTable({
  referrals,
  inspectingCustomerId,
  selectedIds,
  menuOpenId,
  onToggleSelect,
  onToggleSelectAll,
  onToggleMenu,
  onRowOpen,
  onMenuAction,
}: ReferralsTableProps) {
  const user = useAuthStore((s) => s.user);
  const allVisibleSelected = referrals.length > 0 && referrals.every((r) => selectedIds.has(r.id));

  return (
    <div className={menuOpenId ? 'w-full overflow-visible' : 'w-full overflow-x-auto'}>
      <table className="bg-bg-3 border-border-0 w-full min-w-[1000px] border-separate border-spacing-0 rounded-md border">
        <thead>
          <tr>
            <th className="w-9 rounded-tl-md pr-0 pl-1" style={HEADER_STYLE}>
              <Checkbox
                checked={allVisibleSelected}
                ariaLabel="Select all visible referrals"
                onChange={onToggleSelectAll}
              />
            </th>
            {[
              'Customer',
              'Referred by',
              'Product',
              'Note',
              'Submitted',
              'Status',
              'Assigned to',
            ].map((h) => (
              <th key={h} style={HEADER_STYLE} className="px-3.5 py-2.5 text-left">
                {h}
              </th>
            ))}
            <th style={HEADER_STYLE} className="w-12 rounded-tr-md px-3.5 py-2.5 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {referrals.map((r) => {
            const meta = REFERRAL_STATUS_META[r.status];
            const owner = effectiveOwner(r);
            const isNew = r.status === 'pending';
            const inspecting =
              inspectingCustomerId !== null && r.customerId === inspectingCustomerId;
            const selected = selectedIds.has(r.id);
            const myUnread = r.tellerUnread && r.tellerName === user?.displayName;
            const latestEvent = r.events.at(-1);
            return (
              <tr
                key={r.id}
                onClick={() => {
                  onRowOpen(r);
                }}
                title="Open customer profile"
                className={`cursor-pointer ${
                  inspecting
                    ? '[&>td]:bg-brand-dim [&>td]:border-b-[var(--border-accent)] [&>td:first-child]:shadow-[inset_2px_0_0_0_var(--cyan)]'
                    : selected
                      ? '[&>td]:[background:color-mix(in_srgb,var(--cyan)_7%,transparent)] hover:[&>td]:[background:color-mix(in_srgb,var(--cyan)_11%,transparent)]'
                      : 'hover:[&>td]:bg-bg-4'
                }`}
              >
                <td
                  className="border-border-0 w-9 border-b py-3 pr-0 pl-1 text-center align-middle"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Checkbox
                    checked={selected}
                    ariaLabel={`Select referral for ${r.customerName}`}
                    onChange={() => {
                      onToggleSelect(r.id);
                    }}
                  />
                </td>
                <td className={`${CELL} ${isNew ? 'font-semibold' : ''}`}>
                  {isNew && (
                    <span className="bg-brand-primary mr-2 inline-block h-1.5 w-1.5 rounded-full align-middle shadow-[0_0_0_3px_color-mix(in_srgb,var(--cyan)_18%,transparent)]" />
                  )}
                  {myUnread && (
                    <span
                      data-testid="unread-dot"
                      className="bg-brand-primary mr-[7px] inline-block h-1.5 w-1.5 rounded-full align-middle shadow-[0_0_0_3px_color-mix(in_srgb,var(--cyan)_18%,transparent)]"
                    />
                  )}
                  <span className="text-text-0 font-medium">{r.customerName}</span>
                  {myUnread && latestEvent && (
                    <span className="text-brand-primary mt-0.5 block text-[0.6875rem] font-semibold">
                      {eventText(latestEvent)}
                    </span>
                  )}
                </td>
                <td className={`${CELL} text-text-3`}>{r.tellerName}</td>
                <td className={CELL}>{r.productName}</td>
                <td className={`${CELL} max-w-[360px]`}>
                  {r.note ? (
                    <span className="text-text-2 italic">{r.note}</span>
                  ) : (
                    <span className="text-text-3">—</span>
                  )}
                  {r.status === 'denied' && r.denialNote && (
                    <span className="bg-danger-dim border-l-danger rounded-tk-xs text-text-1 mt-1.5 block border-l-2 px-2 py-1.5 text-xs leading-normal not-italic">
                      <span className="text-danger mr-1 font-mono text-[0.625rem] font-semibold tracking-[0.06em] uppercase">
                        Denied:
                      </span>
                      {r.denialNote}
                    </span>
                  )}
                </td>
                <td className={`${CELL} text-text-3 font-mono text-xs`}>
                  {timeAgoLabel(r.submittedDaysAgo)}
                </td>
                <td className={CELL}>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                    <span
                      className={`h-[7px] w-[7px] flex-shrink-0 rounded-full ${MINI_DOT[meta.tint] ?? 'bg-text-3'}`}
                    />
                    <span className={r.status === 'denied' ? 'text-text-3' : 'text-text-1'}>
                      {meta.label}
                    </span>
                  </span>
                </td>
                <td className={CELL}>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={`grid h-6 w-6 flex-none place-items-center rounded-full border font-mono text-[0.625rem] font-bold ${
                        owner.isDefault
                          ? 'text-text-3 bg-bg-2 border-border-1'
                          : 'text-brand-primary border-[color-mix(in_srgb,var(--cyan)_30%,transparent)] [background:color-mix(in_srgb,var(--cyan)_14%,transparent)]'
                      }`}
                    >
                      {initialsOf(owner.name)}
                    </span>
                    <span className="flex flex-col leading-[1.2]">
                      <span
                        className={
                          owner.isDefault
                            ? 'text-text-2 text-[0.8125rem]'
                            : 'text-text-0 text-[0.8125rem]'
                        }
                      >
                        {owner.name}
                      </span>
                      <span className="text-text-3 text-[0.6875rem]">
                        {owner.isDefault ? 'Branch Manager · default' : owner.role}
                      </span>
                    </span>
                  </span>
                </td>
                <td
                  className={`${CELL} text-right`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <span className="relative inline-block">
                    <button
                      type="button"
                      aria-haspopup="true"
                      aria-expanded={menuOpenId === r.id}
                      aria-label={`Actions for ${r.customerName}`}
                      onClick={() => {
                        onToggleMenu(menuOpenId === r.id ? null : r.id);
                      }}
                      className={`rounded-tk-sm inline-flex h-7 w-7 items-center justify-center border transition-all ${
                        menuOpenId === r.id
                          ? 'bg-bg-3 border-border-1 text-text-0'
                          : isNew
                            ? 'bg-bg-4 text-brand-primary hover:bg-brand-dim border-[var(--border-accent)] hover:border-[var(--cyan)]'
                            : 'text-text-3 hover:bg-bg-3 hover:border-border-0 hover:text-text-0 border-transparent'
                      }`}
                    >
                      <svg
                        viewBox="0 0 14 14"
                        width="14"
                        height="14"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <circle cx="2.5" cy="7" r="1.2" />
                        <circle cx="7" cy="7" r="1.2" />
                        <circle cx="11.5" cy="7" r="1.2" />
                      </svg>
                    </button>
                    {menuOpenId === r.id && (
                      <div className="border-border-1 bg-bg-4 rounded-tk-md shadow-tk-md absolute top-[calc(100%+4px)] right-0 z-[100] flex min-w-[220px] flex-col p-1">
                        {menuItemsFor(r.status).map((item) => (
                          <button
                            key={item.action}
                            type="button"
                            title={item.disabled}
                            aria-disabled={Boolean(item.disabled)}
                            onClick={() => {
                              if (item.disabled) return;
                              onToggleMenu(null);
                              onMenuAction(r, item.action);
                            }}
                            className={`rounded-tk-sm flex items-center gap-2 px-2.5 py-[7px] text-left text-[0.8125rem] whitespace-nowrap transition-colors ${
                              item.disabled
                                ? 'cursor-not-allowed opacity-40'
                                : item.kind === 'primary'
                                  ? 'text-status-success mb-0.5 font-semibold [background:rgba(34,178,110,0.10)] hover:[background:rgba(34,178,110,0.18)]'
                                  : item.kind === 'danger'
                                    ? 'text-status-denied hover:[background:rgba(200,74,74,0.08)]'
                                    : 'text-text-1 hover:bg-bg-3 hover:text-text-0'
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Transparent backdrop closes the open menu. */}
      {menuOpenId && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => {
            onToggleMenu(null);
          }}
          className="fixed inset-0 z-[90] cursor-default bg-transparent"
        />
      )}
    </div>
  );
}

const HEADER_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.625rem',
  fontWeight: 600,
  color: 'var(--text-3)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  background: 'var(--bg-4)',
  borderBottom: '1px solid var(--border-0)',
};

const CELL = 'border-border-0 border-b px-3.5 py-3 text-[0.8125rem] text-text-1 align-middle';

function Checkbox({
  checked,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  ariaLabel: string;
  onChange: () => void;
}) {
  return (
    <label className="relative inline-flex cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        aria-label={ariaLabel}
        onChange={onChange}
        className="absolute inset-0 cursor-pointer opacity-0"
      />
      <span
        className={`inline-block h-4 w-4 rounded border-[1.5px] transition-colors ${
          checked
            ? 'bg-brand-primary border-brand-primary after:absolute after:top-[3px] after:left-[6px] after:h-2 after:w-1 after:rotate-45 after:border-r-2 after:border-b-2 after:border-white after:content-[""]'
            : 'border-border-2 bg-bg-4 hover:border-brand-primary'
        }`}
      />
    </label>
  );
}

function eventText(ev: Referral['events'][number]): string {
  switch (ev.type) {
    case 'reassigned':
    case 'released':
      return `Reassigned to ${ev.to ?? 'the Branch Manager'}`;
    case 'denied':
      return 'Denied';
    case 'converted':
      return 'Converted — customer acquired the product';
  }
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '')).toUpperCase();
}
